import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import Input from "../../../components/Input";
import { DatePickerField } from "../../../components/DatePickerField";
import {
  Plus,
  Edit,
  Trash2,
  Star,
  ShieldCheck,
  UserCheck,
  X,
  Save,
} from "lucide-react";
import { destaquesService, Destaque, CreateDestaqueData } from "../../../services/destaquesService";
import { enqueueSnackbar } from "notistack";
import moment from "moment";

// Helpers
const labelCategoria = (cat: string) =>
  cat === "P" ? "Praça" : cat === "O" ? "Oficial" : cat;

const labelStatus = (s: string) => (s === "A" ? "Ativo" : "Inativo");

const categorias = [
  { value: "P", label: "Praça" },
  { value: "O", label: "Oficial" },
];

// Estado inicial do formulário
const formVazio = {
  nick: "",
  data_inicio: "",
  data_fim: "",
  categoria: "P" as "P" | "O",
  status: "A" as "A" | "I",
};

export function Destaques() {
  const [destaques, setDestaques] = useState<Destaque[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Filtro de categoria ativa na view
  const [filtroCategoria, setFiltroCategoria] = useState<"todos" | "P" | "O">("todos");

  // Formulário
  const [showForm, setShowForm] = useState(false);
  const [editingDestaque, setEditingDestaque] = useState<Destaque | null>(null);
  const [form, setForm] = useState(formVazio);

  useEffect(() => {
    fetchDestaques();
  }, []);

  async function fetchDestaques() {
    setLoading(true);
    try {
      const data = await destaquesService.listar();
      setDestaques(data);
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || "Erro ao carregar destaques",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  }

  function handleOpenCreate() {
    setEditingDestaque(null);
    setForm(formVazio);
    setShowForm(true);
  }

  function handleOpenEdit(destaque: Destaque) {
    setEditingDestaque(destaque);
    setForm({
      nick: destaque.nick,
      data_inicio: moment(destaque.data_inicio).format("YYYY-MM-DD"),
      data_fim: moment(destaque.data_fim).format("YYYY-MM-DD"),
      categoria: destaque.categoria as "P" | "O",
      status: destaque.status as "A" | "I",
    });
    setShowForm(true);
  }

  function handleCloseForm() {
    setShowForm(false);
    setEditingDestaque(null);
    setForm(formVazio);
  }

  async function handleSave() {
    if (!form.nick || !form.data_inicio || !form.data_fim || !form.categoria) {
      enqueueSnackbar("Preencha todos os campos obrigatórios.", { variant: "warning" });
      return;
    }

    setSaving(true);
    try {
      if (editingDestaque) {
        await destaquesService.atualizar(editingDestaque.id, {
          nick: form.nick,
          data_inicio: form.data_inicio,
          data_fim: form.data_fim,
          categoria: form.categoria,
          status: form.status,
        });
        enqueueSnackbar("Destaque atualizado com sucesso!", { variant: "success" });
      } else {
        const payload: CreateDestaqueData = {
          nick: form.nick,
          data_inicio: form.data_inicio,
          data_fim: form.data_fim,
          categoria: form.categoria,
        };
        await destaquesService.criar(payload);
        enqueueSnackbar(
          `Destaque de ${labelCategoria(form.categoria)} criado! Caso houvesse um ativo, foi inativado automaticamente.`,
          { variant: "success" }
        );
      }
      handleCloseForm();
      fetchDestaques();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || "Erro ao salvar destaque",
        { variant: "error" }
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm("Tem certeza que deseja excluir este destaque?")) return;

    try {
      await destaquesService.excluir(id);
      enqueueSnackbar("Destaque excluído com sucesso!", { variant: "success" });
      fetchDestaques();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || "Erro ao excluir destaque",
        { variant: "error" }
      );
    }
  }

  // Filtrar destaques conforme aba selecionada
  const destaquesFiltrados =
    filtroCategoria === "todos"
      ? destaques
      : destaques.filter((d) => d.categoria === filtroCategoria);

  // Destaque ativo por categoria
  const ativoPraca = destaques.find((d) => d.categoria === "P" && d.status === "A");
  const ativoOficial = destaques.find((d) => d.categoria === "O" && d.status === "A");

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Star className="w-6 h-6 text-yellow-500" />
            Destaques
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Gerencie os destaques de Praça e Oficial do período.
          </p>
        </div>
        <Button onClick={handleOpenCreate} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Novo Destaque
        </Button>
      </div>

      {/* Cards de destaque ativo */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Praça ativo */}
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-blue-500" />
              Destaque Praça Ativo
            </CardTitle>
          </CardHeader>
          <CardContent>
            {ativoPraca ? (
              <div className="space-y-1">
                <p className="text-lg font-semibold text-foreground">
                  {ativoPraca.nick}
                </p>
                <p className="text-sm text-muted-foreground">
                  {moment(ativoPraca.data_inicio).format("DD/MM/YYYY")} →{" "}
                  {moment(ativoPraca.data_fim).format("DD/MM/YYYY")}
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Nenhum destaque ativo</p>
            )}
          </CardContent>
        </Card>

        {/* Oficial ativo */}
        <Card className="border-l-4 border-l-yellow-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-yellow-500" />
              Destaque Oficial Ativo
            </CardTitle>
          </CardHeader>
          <CardContent>
            {ativoOficial ? (
              <div className="space-y-1">
                <p className="text-lg font-semibold text-foreground">
                  {ativoOficial.nick}
                </p>
                <p className="text-sm text-muted-foreground">
                  {moment(ativoOficial.data_inicio).format("DD/MM/YYYY")} →{" "}
                  {moment(ativoOficial.data_fim).format("DD/MM/YYYY")}
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Nenhum destaque ativo</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Formulário (modal inline) */}
      {showForm && (
        <Card className="border-primary/40 shadow-md">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>
                  {editingDestaque ? "Editar Destaque" : "Novo Destaque"}
                </CardTitle>
                <CardDescription>
                  {editingDestaque
                    ? "Altere os dados do destaque"
                    : "Preencha os dados para registrar um novo destaque. O destaque ativo da mesma categoria será inativado automaticamente."}
                </CardDescription>
              </div>
              <Button variant="ghost" size="icon" onClick={handleCloseForm}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Nick */}
              <div className="md:col-span-2">
                <Input
                  title="Nick"
                  value={form.nick}
                  onChange={(e) => setForm((f) => ({ ...f, nick: e.target.value }))}
                  placeholder="Nick do militar destaque"
                />
              </div>

              {/* Data Início */}
              <div>
                <DatePickerField
                  label="Data Início"
                  value={form.data_inicio}
                  onChange={(val) => setForm((f) => ({ ...f, data_inicio: val }))}
                />
              </div>

              {/* Data Fim */}
              <div>
                <DatePickerField
                  label="Data Fim"
                  value={form.data_fim}
                  onChange={(val) => setForm((f) => ({ ...f, data_fim: val }))}
                  minDate={form.data_inicio ? new Date(form.data_inicio + 'T00:00:00') : undefined}
                />
              </div>

              {/* Categoria */}
              <div>
                <label className="text-sm font-medium text-foreground block mb-1">
                  Categoria
                </label>
                <select
                  value={form.categoria}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      categoria: e.target.value as "P" | "O",
                    }))
                  }
                  className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {categorias.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status (apenas na edição) */}
              {editingDestaque && (
                <div>
                  <label className="text-sm font-medium text-foreground block mb-1">
                    Status
                  </label>
                  <select
                    value={form.status}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        status: e.target.value as "A" | "I",
                      }))
                    }
                    className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="A">Ativo</option>
                    <option value="I">Inativo</option>
                  </select>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <Button onClick={handleSave} disabled={saving} className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                {saving ? "Salvando..." : editingDestaque ? "Atualizar" : "Criar Destaque"}
              </Button>
              <Button variant="outline" onClick={handleCloseForm}>
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabela de destaques */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle>Histórico de Destaques</CardTitle>
              <CardDescription>
                Todos os registros de destaque de praça e oficial
              </CardDescription>
            </div>
            {/* Filtros de categoria */}
            <div className="flex gap-2">
              {(["todos", "P", "O"] as const).map((cat) => (
                <button
                  key={cat}
                  onClick={() => setFiltroCategoria(cat)}
                  className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                    filtroCategoria === cat
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                  }`}
                >
                  {cat === "todos" ? "Todos" : cat === "P" ? "Praça" : "Oficial"}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              Carregando...
            </div>
          ) : destaquesFiltrados.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
              <Star className="w-8 h-8 opacity-30" />
              <p className="text-sm">Nenhum destaque encontrado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-6 py-3 font-medium text-muted-foreground">Nick</th>
                    <th className="text-left px-6 py-3 font-medium text-muted-foreground">Categoria</th>
                    <th className="text-left px-6 py-3 font-medium text-muted-foreground">Data Início</th>
                    <th className="text-left px-6 py-3 font-medium text-muted-foreground">Data Fim</th>
                    <th className="text-left px-6 py-3 font-medium text-muted-foreground">Status</th>
                    <th className="text-right px-6 py-3 font-medium text-muted-foreground">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {destaquesFiltrados.map((destaque) => (
                    <tr
                      key={destaque.id}
                      className="border-b border-border/50 hover:bg-muted/20 transition-colors"
                    >
                      <td className="px-6 py-3 font-medium text-foreground">
                        {destaque.nick}
                      </td>
                      <td className="px-6 py-3">
                        <Badge
                          variant="outline"
                          className={
                            destaque.categoria === "O"
                              ? "border-yellow-500 text-yellow-600 dark:text-yellow-400"
                              : "border-blue-500 text-blue-600 dark:text-blue-400"
                          }
                        >
                          {destaque.categoria === "P" ? (
                            <UserCheck className="w-3 h-3 mr-1" />
                          ) : (
                            <ShieldCheck className="w-3 h-3 mr-1" />
                          )}
                          {labelCategoria(destaque.categoria)}
                        </Badge>
                      </td>
                      <td className="px-6 py-3 text-muted-foreground">
                        {moment(destaque.data_inicio).format("DD/MM/YYYY")}
                      </td>
                      <td className="px-6 py-3 text-muted-foreground">
                        {moment(destaque.data_fim).format("DD/MM/YYYY")}
                      </td>
                      <td className="px-6 py-3">
                        <Badge
                          variant={destaque.status === "A" ? "default" : "secondary"}
                          className={
                            destaque.status === "A"
                              ? "bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/30"
                              : "bg-muted text-muted-foreground"
                          }
                        >
                          {destaque.status} — {labelStatus(destaque.status)}
                        </Badge>
                      </td>
                      <td className="px-6 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenEdit(destaque)}
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(destaque.id)}
                            title="Excluir"
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
